import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, ChevronRight, ChevronLeft, Trophy, X } from "lucide-react";
import { toast } from "sonner";
import { fmtCurrency } from "@/lib/utils";

const COLOR_BAR = {
  muted: "bg-muted-foreground/40",
  amber: "bg-amber-accent",
  signal: "bg-signal-500",
  emerald: "bg-emerald-500",
  destructive: "bg-destructive",
};

export default function PipelinePage() {
  const [pipeline, setPipeline] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pipeline");
      setPipeline(data);
    } catch { toast.error("Failed to load pipeline"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (prospectId, toStage) => {
    try {
      await api.post(`/pipeline/move?prospect_id=${prospectId}&to_stage=${toStage}`);
      await load();
    } catch { toast.error("Move failed"); }
  };

  if (!pipeline) return <DashboardLayout eyebrow="Pipeline" title="Deal Pipeline" />;

  const stages = pipeline.columns;
  const totals = pipeline.totals || {};

  return (
    <DashboardLayout eyebrow="Pipeline" title="Deal Pipeline">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 grid-borders mb-7" data-testid="pipeline-totals">
        <KPI label="Open pipeline" value={fmtCurrency(totals.pipeline || 0)} sub={`${totals.prospects} prospects`} />
        <KPI label="Won" value={fmtCurrency(totals.won || 0)} sub="closed deals" accent="text-emerald-500" />
        <KPI label="All-time value" value={fmtCurrency(totals.all || 0)} sub="open + closed" />
        <KPI label="Conversion" value={`${stages.find((s) => s.stage.key === "won")?.count || 0} / ${stages.find((s) => s.stage.key !== "new")?.count || 0}`} sub="won / contacted+" />
      </div>

      {/* Horizontal scroll pipeline */}
      <div className="overflow-x-auto pb-3" data-testid="pipeline-board">
        <div className="flex gap-3 min-w-max">
          {stages.map((col, idx) => (
            <div key={col.stage.key} className="w-72 flex-shrink-0 bg-card/40 border border-border rounded-sm" data-testid={`pipeline-col-${col.stage.key}`}>
              <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full ${COLOR_BAR[col.stage.color] || COLOR_BAR.muted}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider">{col.stage.label}</span>
                  <span className="text-[0.65rem] text-muted-foreground">· {col.count}</span>
                </div>
                {col.deal_value_total > 0 && (
                  <span className="text-[0.65rem] font-mono text-muted-foreground">{fmtCurrency(col.deal_value_total)}</span>
                )}
              </div>
              <div className="p-2 space-y-2 max-h-[68vh] overflow-y-auto">
                {col.items.length === 0 && <div className="text-[0.65rem] text-muted-foreground italic py-6 text-center">empty</div>}
                {col.items.map((p) => (
                  <PipelineCard
                    key={p.id}
                    p={p}
                    canBack={idx > 0}
                    canFwd={idx < stages.length - 1}
                    onBack={() => move(p.id, stages[idx - 1].stage.key)}
                    onFwd={() => move(p.id, stages[idx + 1].stage.key)}
                    onWon={() => move(p.id, "won")}
                    onLost={() => move(p.id, "lost")}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className="p-6 flex flex-col gap-1.5 bg-card/40">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-2xl font-semibold tracking-tightest ${accent || ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function PipelineCard({ p, canBack, canFwd, onBack, onFwd, onWon, onLost }) {
  return (
    <div className="bg-background border border-border rounded-sm p-2.5 hover:border-signal-500/30 transition-colors" data-testid={`pipeline-card-${p.id}`}>
      <Link to={`/app/prospects/${p.id}`} className="block">
        <div className="font-medium text-sm truncate hover:underline inline-flex items-center gap-1">
          {p.full_name} <ArrowUpRight className="w-3 h-3" />
        </div>
        <div className="text-xs text-muted-foreground truncate">{p.title}</div>
        <div className="text-xs text-muted-foreground truncate">{p.company}</div>
      </Link>
      {(p.deal_value || p.next_step) && (
        <div className="mt-1.5 flex flex-wrap gap-1 text-[0.65rem]">
          {p.deal_value > 0 && <span className="signal-pill">{fmtCurrency(p.deal_value)}</span>}
          {p.next_step && <span className="signal-pill truncate max-w-full">↗ {p.next_step}</span>}
        </div>
      )}
      <div className="mt-2 flex gap-1">
        {canBack && (
          <Button size="sm" variant="ghost" onClick={onBack} className="h-6 px-1.5" data-testid={`pipeline-back-${p.id}`} aria-label="back">
            <ChevronLeft className="w-3 h-3" />
          </Button>
        )}
        {canFwd && (
          <Button size="sm" variant="ghost" onClick={onFwd} className="h-6 px-1.5" data-testid={`pipeline-fwd-${p.id}`} aria-label="forward">
            <ChevronRight className="w-3 h-3" />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onWon} className="h-6 px-1.5 text-emerald-500 hover:text-emerald-500" data-testid={`pipeline-won-${p.id}`} aria-label="won">
          <Trophy className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onLost} className="h-6 px-1.5 text-destructive hover:text-destructive" data-testid={`pipeline-lost-${p.id}`} aria-label="lost">
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
